import React, { useState, useEffect } from "react";
import { empresaService } from "../services/api";
import {
    Card,
    Form,
    Button,
    Row,
    Col,
    Badge,
    Table,
    Pagination,
} from "react-bootstrap";
import { FaSearch, FaUpload, FaTrash, FaPaperPlane, FaDownload, FaHistory } from "react-icons/fa";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";

// Función para formatear fechas a DD/MM/YYYY
const formatDate = (dateString) => {
    if (!dateString) return '';
    // Si es un string vacío o solo espacios, retornar vacío
    if (typeof dateString === 'string' && dateString.trim() === '') return '';
    // Intentar parsear la fecha
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        // Si no es una fecha válida, intentar otros formatos comunes
        console.warn('Fecha no válida:', dateString);
        return '';
    }
    return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Función para convertir fechas al formato YYYY-MM-DD para el input type="date"
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
};

const Empresa = () => {
    const [loading, setLoading] = useState(false);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [documentosEmpresa, setDocumentosEmpresa] = useState([]);
    const [documentosVisibles, setDocumentosVisibles] = useState({});
    const [documentosSeleccionados, setDocumentosSeleccionados] = useState({});
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [currentDocumentType, setCurrentDocumentType] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [validityDate, setValidityDate] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [currentHistoryDocumentType, setCurrentHistoryDocumentType] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchTiposDocumentos();
    }, []);

    useEffect(() => {
        if (tiposDocumentos.length > 0) {
            fetchCompanyDocuments(tiposDocumentos);
        }
    }, [tiposDocumentos]);

    // Función para obtener tipos de documentos desde el procedimiento almacenado
    const fetchTiposDocumentos = async () => {
        try {
            const res = await empresaService.getDocumentTypes();
            if (res.data.success && res.data.documentTypes) {
                const tipos = res.data.documentTypes;
                setTiposDocumentos(tipos);

                // Inicializar estados dinámicamente basados en los tipos obtenidos
                const visibles = {};
                const seleccionados = {};

                tipos.forEach(tipo => {
                    visibles[tipo.ID] = false;
                    seleccionados[tipo.ID] = false;
                });

                setDocumentosVisibles(visibles);
                setDocumentosSeleccionados(seleccionados);
            }
        } catch (err) {
            console.error("Error al cargar tipos de documentos:", err);
            toast.error("Error al cargar los tipos de documentos");
        }
    };

    // Función para obtener documentos asociados a la empresa
    const fetchCompanyDocuments = async (tipos) => {
        try {
            const res = await empresaService.getCompanyDocuments();
            if (res.data.success && res.data.companyDocuments) {
                const empresaDocs = res.data.companyDocuments;
                setDocumentosEmpresa(empresaDocs);

                // Actualizar el estado de documentosVisibles basado en los documentos obligatorios
                setDocumentosVisibles(prev => {
                    const nuevosVisibles = { ...prev };

                    empresaDocs.forEach(doc => {
                        if (doc.OBLIGATORIO === true || doc.OBLIGATORIO === 1) {
                            nuevosVisibles[doc.DOCUMENTO_TIPO_ID] = true;
                        }
                    });

                    return nuevosVisibles;
                });
            }
        } catch (err) {
            console.error("Error al cargar documentos de empresa:", err);
        }
    };

    // Funciones para manejar acciones de documentos
    const handleUploadDocument = (documentType) => {
        setCurrentDocumentType(documentType);

        // Verificar si el documento no requiere renovación
        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === documentType);
        const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

        if (esDocumentoSinRenovacion) {
            setValidityDate(null);
        } else if (tipoDocumento && tipoDocumento.MESES_HASTA_PROX_RENOVACION) {
            const fechaActual = new Date();
            const fechaValidez = new Date(fechaActual);
            fechaValidez.setMonth(fechaValidez.getMonth() + tipoDocumento.MESES_HASTA_PROX_RENOVACION);
            const fechaFormateada = fechaValidez.toISOString().split('T')[0];
            setValidityDate(fechaFormateada);
        } else {
            setValidityDate('');
        }

        setShowUploadModal(true);
    };

    const handleDeleteDocument = async (documentType) => {
        try {
            if (!window.confirm('¿Estás seguro de que quieres eliminar este fichero? Esta acción no se puede deshacer.')) {
                return;
            }

            setLoading(true);
            const response = await empresaService.deleteDocumentFile(documentType);

            if (response.data.success) {
                toast.success('Fichero eliminado correctamente');
                if (tiposDocumentos.length > 0) {
                    await fetchCompanyDocuments(tiposDocumentos);
                }
            } else {
                toast.error(response.data.message || 'Error al eliminar el fichero');
            }
        } catch (error) {
            console.error('Error eliminando fichero del documento:', error);
            toast.error(error.response?.data?.message || 'Error al eliminar el fichero');
        } finally {
            setLoading(false);
        }
    };

    // Función para manejar la selección de archivo
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast.error('Solo se permiten archivos PDF');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error('El archivo no puede ser mayor a 10MB');
                return;
            }
            setSelectedFile(file);

            if (currentDocumentType) {
                const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

                if (esDocumentoSinRenovacion) {
                    setValidityDate(null);
                } else if (tipoDocumento && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                    const fechaActual = new Date();
                    const fechaValidez = new Date(fechaActual);
                    fechaValidez.setMonth(fechaValidez.getMonth() + tipoDocumento.MESES_HASTA_PROX_RENOVACION);
                    const fechaFormateada = fechaValidez.toISOString().split('T')[0];
                    setValidityDate(fechaFormateada);
                } else {
                    setValidityDate('');
                }
            }
        }
    };

    // Función para subir el documento
    const handleUploadFile = async () => {
        if (!selectedFile) {
            toast.error('Por favor selecciona un archivo');
            return;
        }

        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
        const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

        if (!esDocumentoSinRenovacion) {
            if (!validityDate) {
                toast.error('Por favor selecciona una fecha de validez');
                return;
            }

            const fechaValidez = new Date(validityDate);
            const fechaActual = new Date();
            fechaActual.setHours(0, 0, 0, 0);

            if (fechaValidez < fechaActual) {
                toast.error('La fecha de validez no puede ser inferior a la fecha actual');
                return;
            }
        }

        setUploadingDocument(true);
        try {
            const fechaAEnviar = esDocumentoSinRenovacion ? null : (validityDate || null);
            const response = await empresaService.uploadDocument(currentDocumentType, selectedFile, fechaAEnviar);

            if (response.data.success) {
                toast.success('Documento subido correctamente');
                setShowUploadModal(false);
                setSelectedFile(null);
                setValidityDate(null);

                if (tiposDocumentos.length > 0) {
                    await fetchCompanyDocuments(tiposDocumentos);
                }
            } else {
                toast.error(response.data.message || 'Error al subir el documento');
            }
        } catch (error) {
            console.error('Error subiendo documento:', error);
            toast.error(error.response?.data?.message || 'Error al subir el documento');
        } finally {
            setUploadingDocument(false);
        }
    };

    // Función para manejar la selección/deselección de tarjetas de documentos
    const handleDocumentCardClick = (documentType) => {
        setDocumentosSeleccionados(prev => ({
            ...prev,
            [documentType]: !prev[documentType]
        }));
    };

    // Función para descargar documentos seleccionados
    const handleDownloadSelected = async () => {
        const documentosParaDescargar = Object.entries(documentosSeleccionados)
            .filter(([_, isSelected]) => isSelected)
            .map(([docType, _]) => {
                const empresaDoc = documentosEmpresa.find(doc =>
                    doc.DOCUMENTO_TIPO_ID === docType.toString() && doc.FICHERO
                );
                return empresaDoc;
            })
            .filter(doc => doc);

        if (documentosParaDescargar.length === 0) {
            toast.warning('No hay documentos seleccionados con ficheros para descargar');
            return;
        }

        try {
            setLoading(true);
            toast.info(`Preparando descarga de ${documentosParaDescargar.length} documento(s) seleccionado(s)`);

            const response = await empresaService.downloadSelectedDocuments(documentosParaDescargar);

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `documentos_empresa.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Descarga completada: ${documentosParaDescargar.length} documento(s)`);
        } catch (error) {
            console.error('Error descargando documentos:', error);
            toast.error('Error al descargar los documentos seleccionados');
        } finally {
            setLoading(false);
        }
    };

    // Función para cargar el historial desde el backend
    const loadHistory = async (documentType, fechaDesdeParam = null, fechaHastaParam = null) => {
        setLoadingHistory(true);
        try {
            const res = await empresaService.getDocumentHistory(documentType, fechaDesdeParam, fechaHastaParam);
            if (res.data.success && res.data.history) {
                setFilteredHistory(res.data.history);
            } else {
                setFilteredHistory([]);
            }
        } catch (error) {
            console.error('Error cargando historial:', error);
            toast.error('Error al cargar el historial de documentos');
            setFilteredHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Función para abrir modal de historial
    const handleViewHistory = async (documentType) => {
        setCurrentHistoryDocumentType(documentType);
        setShowHistoryModal(true);
        setFechaDesde('');
        setFechaHasta('');
        setCurrentPage(1);
        setFilteredHistory([]);

        await loadHistory(documentType);
    };

    // Función para filtrar el historial por fechas (llama al backend)
    const applyDateFilter = async () => {
        if (!currentHistoryDocumentType) return;

        setCurrentPage(1); // Resetear a la primera página al filtrar
        await loadHistory(currentHistoryDocumentType, fechaDesde || null, fechaHasta || null);
    };

    // Función para limpiar filtros
    const clearDateFilter = async () => {
        setFechaDesde('');
        setFechaHasta('');
        setCurrentPage(1);
        if (currentHistoryDocumentType) {
            await loadHistory(currentHistoryDocumentType);
        }
    };

    // Calcular datos paginados
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

    // Función para cambiar de página
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Función para descargar todos los documentos
    const handleDownloadAll = async () => {
        const documentosConFicheros = documentosEmpresa.filter(doc => doc.FICHERO) || [];

        if (documentosConFicheros.length === 0) {
            toast.warning('No hay documentos con ficheros para descargar');
            return;
        }

        try {
            setLoading(true);
            toast.info(`Preparando descarga de todos los documentos (${documentosConFicheros.length})`);

            const response = await empresaService.downloadAllDocuments();

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Documents_Empresa.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Descarga completada: ${documentosConFicheros.length} documento(s)`);
        } catch (error) {
            console.error('Error descargando todos los documentos:', error);
            toast.error('Error al descargar todos los documentos');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-4">
            <Card>
                <Card.Header>
                    <h4 className="mb-0">Documentos de Empresa</h4>
                </Card.Header>
                <Card.Body>
                    {/* Panel de Documentos en formato de tarjetas */}
                    <div className="mt-4 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Documentos</h5>
                            <div className="d-flex align-items-center gap-2">
                                <Badge
                                    bg={Object.values(documentosSeleccionados).some(selected => selected) ? 'success' : 'secondary'}
                                    className="fs-6"
                                >
                                    {Object.values(documentosSeleccionados).filter(selected => selected).length} de {Object.keys(documentosSeleccionados).length} documentos seleccionados
                                </Badge>
                                {Object.values(documentosSeleccionados).some(selected => selected) && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            const resetSeleccionados = {};
                                            tiposDocumentos.forEach(tipo => {
                                                resetSeleccionados[tipo.ID] = false;
                                            });
                                            setDocumentosSeleccionados(resetSeleccionados);
                                        }}
                                    >
                                        Deseleccionar Todos
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Botones de descarga */}
                        <div className="d-flex justify-content-end gap-2 mb-3">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleDownloadSelected}
                                disabled={Object.values(documentosSeleccionados).filter(selected => selected).length === 0}
                            >
                                <FaDownload /> Descargar Seleccionados
                            </Button>
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={handleDownloadAll}
                                disabled={loading}
                            >
                                <FaDownload /> Descargar Todo
                            </Button>
                        </div>
                        <Row>
                            {/* Tarjetas de documentos generadas dinámicamente */}
                            {tiposDocumentos.map((tipo) => (
                                <Col md={6} lg={4} className="mb-3" key={tipo.ID}>
                                    <Card
                                        className={`h-100 ${documentosSeleccionados[tipo.ID] ? 'border-success' : ''}`}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: documentosSeleccionados[tipo.ID] ? '#d4edda' : 'white',
                                            borderColor: documentosSeleccionados[tipo.ID] ? '#28a745' : '#dee2e6',
                                            transition: 'all 0.3s ease',
                                            transform: documentosSeleccionados[tipo.ID] ? 'scale(1.02)' : 'scale(1)'
                                        }}
                                        onClick={() => handleDocumentCardClick(tipo.ID)}
                                        onMouseEnter={(e) => {
                                            if (!documentosSeleccionados[tipo.ID]) {
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                e.currentTarget.style.transform = 'scale(1.01)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!documentosSeleccionados[tipo.ID]) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }
                                        }}
                                    >
                                        <Card.Header className={documentosSeleccionados[tipo.ID] ? 'bg-success text-white' : 'bg-primary text-white'}>
                                            <h6 className="mb-0">{tipo.NOMBRE}</h6>
                                        </Card.Header>
                                        <Card.Body className="d-flex flex-column">
                                            <div className="mb-3">
                                                {(() => {
                                                    const empresaDoc = documentosEmpresa.find(doc =>
                                                        doc.DOCUMENTO_TIPO_ID === tipo.ID.toString()
                                                    );

                                                    const esDocumentoSinRenovacion = tipo.DOCUMENTO_SIN_RENOVACION === true || tipo.DOCUMENTO_SIN_RENOVACION === 1;

                                                    if (esDocumentoSinRenovacion) {
                                                        if (empresaDoc?.FICHERO) {
                                                            return (
                                                                <>
                                                                    <div className="mb-2">
                                                                        <strong>Fichero:</strong> {empresaDoc.FICHERO || 'Sin nombre'}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Estado:</strong>
                                                                        <span className="ms-2 badge bg-success text-white">
                                                                            Documento subido
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            );
                                                        } else {
                                                            return (
                                                                <div>
                                                                    <strong>Estado:</strong>
                                                                    <span className="ms-2 badge bg-warning text-white">
                                                                        Documento sin subir
                                                                    </span>
                                                                </div>
                                                            );
                                                        }
                                                    } else if (empresaDoc?.HASTA) {
                                                        const fechaValidez = new Date(empresaDoc.HASTA);
                                                        const fechaActual = new Date();
                                                        const diferenciaDias = Math.ceil((fechaValidez - fechaActual) / (1000 * 60 * 60 * 24));
                                                        const diasAvisoCaducaPronto = tipo.DIAS_AVISO_CADUCA_PRONTO ?? 30;

                                                        let colorValidez = '';
                                                        let textoValidez = '';

                                                        if (diferenciaDias < 0) {
                                                            colorValidez = 'text-danger';
                                                            textoValidez = 'VENCIDO';
                                                        } else if (diferenciaDias <= diasAvisoCaducaPronto) {
                                                            colorValidez = 'text-warning';
                                                            textoValidez = 'VENCE PRONTO';
                                                        } else {
                                                            colorValidez = 'text-success';
                                                            textoValidez = 'VÁLIDO';
                                                        }

                                                        return (
                                                            <>
                                                                <div className="mb-2">
                                                                    <strong>Fichero:</strong> {empresaDoc.FICHERO || 'Sin nombre'}
                                                                </div>
                                                                <div>
                                                                    <strong>Fecha de Validez:</strong> {formatDate(empresaDoc.HASTA)}
                                                                    <span className={`ms-2 badge ${colorValidez === 'text-danger' ? 'bg-danger' : colorValidez === 'text-warning' ? 'bg-warning' : 'bg-success'} text-white`}>
                                                                        {textoValidez}
                                                                    </span>
                                                                </div>
                                                                <div className={`mt-1 small ${colorValidez}`}>
                                                                    {diferenciaDias < 0
                                                                        ? `Vencido hace ${Math.abs(diferenciaDias)} día(s)`
                                                                        : diferenciaDias === 0
                                                                            ? 'Vence hoy'
                                                                            : `Vence en ${diferenciaDias} día(s)`
                                                                    }
                                                                </div>
                                                            </>
                                                        );
                                                    } else {
                                                        return (
                                                            <div>
                                                                <strong>Fecha de Validez:</strong> Sin fecha
                                                            </div>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                            <div className="mt-auto">
                                                <div className="d-grid gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleUploadDocument(tipo.ID);
                                                        }}
                                                    >
                                                        <FaUpload /> Subir fichero
                                                    </Button>
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewHistory(tipo.ID);
                                                        }}
                                                    >
                                                        <FaHistory /> Ver historial
                                                    </Button>
                                                    {(() => {
                                                        const empresaDoc = documentosEmpresa.find(doc =>
                                                            doc.DOCUMENTO_TIPO_ID === tipo.ID.toString()
                                                        );
                                                        return empresaDoc?.FICHERO;
                                                    })() && (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteDocument(tipo.ID);
                                                                }}
                                                            >
                                                                <FaTrash /> Eliminar Fichero
                                                            </Button>
                                                        )}
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </Card.Body>
            </Card>

            {/* Modal para subir archivo */}
            <Modal show={showUploadModal} onHide={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                setValidityDate(null);
            }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Subir Documento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentDocumentType && (() => {
                        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                        if (tipoDocumento) {
                            if (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1) {
                                return (
                                    <div className="alert alert-warning mb-3">
                                        <small>
                                            <strong>Información:</strong> Este documento no tiene fecha de caducidad.
                                            <br />
                                            <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                                        </small>
                                    </div>
                                );
                            } else if (tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                                return (
                                    <div className="alert alert-info mb-3">
                                        <small>
                                            <strong>Información:</strong> La fecha de validez se calculará automáticamente sumando {tipoDocumento.MESES_HASTA_PROX_RENOVACION} mes(es) a la fecha actual.
                                            <br />
                                            <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                                        </small>
                                    </div>
                                );
                            }
                        }
                        return null;
                    })()}
                    <Form.Group className="mb-3">
                        <Form.Label>Seleccionar archivo PDF</Form.Label>
                        <Form.Control
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileSelect}
                        />
                        {selectedFile && (
                            <Form.Text className="text-muted">
                                Archivo seleccionado: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Form.Text>
                        )}
                    </Form.Group>
                    {currentDocumentType && (() => {
                        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                        const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);
                        if (!esDocumentoSinRenovacion) {
                            return (
                                <Form.Group className="mb-3">
                                    <Form.Label>Fecha de validez</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={validityDate || ''}
                                        onChange={(e) => setValidityDate(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            );
                        }
                        return null;
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowUploadModal(false);
                        setSelectedFile(null);
                        setValidityDate(null);
                    }}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleUploadFile} disabled={!selectedFile || uploadingDocument}>
                        {uploadingDocument ? 'Subiendo...' : 'Subir'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para ver historial de documentos */}
            <Modal show={showHistoryModal} onHide={() => {
                setShowHistoryModal(false);
                setCurrentHistoryDocumentType(null);
                setFilteredHistory([]);
                setFechaDesde('');
                setFechaHasta('');
                setCurrentPage(1);
            }} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historial de Documentos</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingHistory ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-2">Cargando historial...</p>
                        </div>
                    ) : (
                        <>
                            {/* Filtros de fecha */}
                            <Card className="mb-3">
                                <Card.Header>
                                    <h6 className="mb-0">Filtros de Fecha</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Fecha Desde</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={fechaDesde}
                                                    onChange={(e) => setFechaDesde(e.target.value)}
                                                    disabled={loadingHistory}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Fecha Hasta</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={fechaHasta}
                                                    onChange={(e) => setFechaHasta(e.target.value)}
                                                    disabled={loadingHistory}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <div className="d-flex gap-2" style={{ marginTop: '40px' }}>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={applyDateFilter}
                                                    disabled={loadingHistory}
                                                >
                                                    <FaSearch /> Filtrar
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={clearDateFilter}
                                                    disabled={loadingHistory}
                                                >
                                                    Limpiar
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Tabla de historial */}
                            {filteredHistory.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    {fechaDesde || fechaHasta
                                        ? 'No hay registros que coincidan con los filtros seleccionados'
                                        : 'No hay historial disponible para este documento'}
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Fecha de Subida</th>
                                                    <th>Fecha de Inicio</th>
                                                    <th>Fecha de Fin</th>
                                                    <th>Fichero</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((item, index) => {
                                                    // Debug: verificar qué valores tiene el item para el primer registro
                                                    if (index === 0) {
                                                        console.log('Primer item del histórico:', item);
                                                        console.log('Campo HASTA:', item.HASTA, 'Tipo:', typeof item.HASTA);
                                                        console.log('Todos los campos del item:', Object.keys(item));
                                                    }

                                                    // Manejar el campo HASTA de manera más robusta
                                                    let fechaHasta = '';
                                                    if (item.HASTA !== null && item.HASTA !== undefined) {
                                                        // Intentar formatear la fecha
                                                        const fechaFormateada = formatDate(item.HASTA);
                                                        fechaHasta = fechaFormateada || String(item.HASTA);
                                                    }

                                                    return (
                                                        <tr key={index}>
                                                            <td>{formatDate(item.FECHA_SUBIDA || item.CREATE_DATE || item.DESDE)}</td>
                                                            <td>{formatDate(item.DESDE || item.FECHA_INICIO)}</td>
                                                            <td>{fechaHasta}</td>
                                                            <td>
                                                                <small className="text-muted">
                                                                    {item.FICHERO || 'Sin fichero'}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                {item.FICHERO && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={async () => {
                                                                            try {
                                                                                // Descargar archivo del historial usando el servicio API
                                                                                const response = await empresaService.downloadHistoryFile(item.FICHERO);
                                                                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                                                                const link = document.createElement('a');
                                                                                link.href = url;
                                                                                // Intentar reconstruir nombre original después del primer '_'
                                                                                const idx = item.FICHERO.indexOf('_');
                                                                                const originalName = idx > -1 ? item.FICHERO.substring(idx + 1) : item.FICHERO;
                                                                                link.setAttribute('download', originalName);
                                                                                document.body.appendChild(link);
                                                                                link.click();
                                                                                link.remove();
                                                                                window.URL.revokeObjectURL(url);
                                                                            } catch (error) {
                                                                                console.error('Error descargando archivo del historial:', error);
                                                                                toast.error('Error al descargar el archivo');
                                                                            }
                                                                        }}
                                                                    >
                                                                        <FaDownload /> Descargar
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Paginación */}
                                    {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="text-muted">
                                                Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredHistory.length)} de {filteredHistory.length} registro(s)
                                            </div>
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage === 1}
                                                />
                                                <Pagination.Prev
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                />
                                                {[...Array(totalPages)].map((_, index) => {
                                                    const pageNumber = index + 1;
                                                    // Mostrar solo algunas páginas alrededor de la actual
                                                    if (
                                                        pageNumber === 1 ||
                                                        pageNumber === totalPages ||
                                                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                                    ) {
                                                        return (
                                                            <Pagination.Item
                                                                key={pageNumber}
                                                                active={pageNumber === currentPage}
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </Pagination.Item>
                                                        );
                                                    } else if (
                                                        pageNumber === currentPage - 2 ||
                                                        pageNumber === currentPage + 2
                                                    ) {
                                                        return <Pagination.Ellipsis key={pageNumber} />;
                                                    }
                                                    return null;
                                                })}
                                                <Pagination.Next
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                />
                                                <Pagination.Last
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowHistoryModal(false);
                        setCurrentHistoryDocumentType(null);
                        setFilteredHistory([]);
                        setFechaDesde('');
                        setFechaHasta('');
                        setCurrentPage(1);
                    }}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Empresa;
